import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { motion, AnimatePresence } from 'framer-motion';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoutePractitioner: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const {isAdmin, clinic, isLoading:isClinicLoading} = useClinic();
  const location = useLocation();

  const showLoading = isLoading || isClinicLoading;

  // Redirect to login if not authenticated
  if (!showLoading && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to clinic selection if practitioner has multiple clinics but no clinic selected
  if (!showLoading && isAuthenticated && user.hasMultipleClinics && !clinic) {
    return <Navigate to="/clinic-selection" replace />;
  }

  if (!showLoading && isAuthenticated && isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <AnimatePresence>
        {showLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-0 z-[9999] min-h-screen bg-white flex items-center justify-center"
          >
            <div className="text-center">
              <img
                src="/nimbus_logo.svg"
                alt="Nimbus"
                className="h-8 mx-auto mb-6"
              />
              <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-teal animate-spin mx-auto"></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!showLoading && isAuthenticated && !isAdmin && children}
    </>
  );
};

export default ProtectedRoutePractitioner; 